// Shared GET requests for data that several mounted components load at once.
//
// On one page load App, the video page and Home each fetched /get-videos, and
// useActivity + useWatchHistory each fetched the same /get-contact/id/... URL
// (QA 10-05: 4 and 6 calls per load). Callers that ask for the same path while
// a request is in flight now share it; the video catalog is also reused for
// a short time. A settled response is reused only by a caller that asks for
// it (reuseMs), and forgetShared() drops it after a write, so watch history
// stays fresh. clearSharedRequests() runs on sign-out.

const CATALOG_CACHE_MS = 60 * 1000;
const inFlight = new Map(); // path -> promise
const settled = new Map(); // path -> {response, at}: last successful answer
let catalog = null; // {promise, settledAt}

export const clearSharedRequests = () => {
  inFlight.clear();
  settled.clear();
  catalog = null;
};

// Forget kept answers for every path starting with `prefix` (after a write
// to that record). Requests still in flight are dropped too: they were read
// before the write, so later callers start a fresh request and their answers
// are not kept (sharedGet keeps an answer only if its request is current).
export const forgetShared = (prefix) => {
  for (const map of [settled, inFlight]) {
    for (const path of [...map.keys()]) {
      if (path.startsWith(prefix)) map.delete(path);
    }
  }
};

// One request per path while it is in flight. With reuseMs, a successful
// answer that settled less than reuseMs ago is returned instead of a new
// request: on a full reload of Account the sidebar's /get-contact settles
// about a second before Account mounts (it waits on /my-case), so the two
// could not share it in flight (QA 10-16).
export const sharedGet = (apiFetch, path, {reuseMs = 0} = {}) => {
  const pending = inFlight.get(path);
  if (pending) return pending;
  const last = settled.get(path);
  if (reuseMs > 0 && last && Date.now() - last.at < reuseMs) {
    return Promise.resolve(last.response);
  }
  const promise = apiFetch(path)
    .then((response) => {
      // Not current any more: forgetShared() or sign-out ran meanwhile.
      if (response?.result === "success" && inFlight.get(path) === promise) {
        settled.set(path, {response, at: Date.now()});
      }
      return response;
    })
    .finally(() => {
      if (inFlight.get(path) === promise) inFlight.delete(path);
    });
  inFlight.set(path, promise);
  return promise;
};

// GET /get-videos, shared in flight and reused for CATALOG_CACHE_MS.
// Failed or unsuccessful responses are not kept, so the next caller retries.
export const getVideoCatalog = (apiFetch) => {
  const fresh =
    catalog &&
    (catalog.settledAt === null ||
      Date.now() - catalog.settledAt < CATALOG_CACHE_MS);
  if (fresh) return catalog.promise;

  const entry = {settledAt: null};
  entry.promise = apiFetch("/get-videos", {method: "GET"}).then(
    (res) => {
      if (res?.result === "success") entry.settledAt = Date.now();
      else if (catalog === entry) catalog = null;
      return res;
    },
    (err) => {
      if (catalog === entry) catalog = null;
      throw err;
    },
  );
  catalog = entry;
  return entry.promise;
};
